import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MessageSquare, Eye, Edit3 } from 'lucide-react';
import MarkdownPreview from './MarkdownPreview';
import Comment from './Comment';
import CommentInput from './CommentInput';
import CopyButton from './CopyButton';
import {
  createComment,
  subscribeToTopLevelComments,
  subscribeToTopLevelCommentsForCoin,
  createCoinComment,
  getReplies,
  getMentionedComments,
  getTopLevelComments,
  voteComment,
  editComment,
  deleteComment,
  flagComment,
  getUserVotesBatch,
  countMentions
} from './commentService';
// COMMENTED OUT: Verified Interactor feature - too heavy (scans all transaction history)
// import { checkMultipleInteractions } from './etherscanService';
import { getAllAliases } from './aliasService';
import { getWatchedAddresses } from './userService';
import { getHighlightCommentFromURL, updateURL, buildAddressUrl } from './urlUtils';
import { trackCommentCreate, trackVote, trackContentFlag } from './analytics';
import { checkRateLimit, recordAction } from './rateLimitService';
import { formatAddress } from './addressUtils';
import { getChain } from './chainUtils';

const CommentSection = ({
  // For addresses (existing)
  targetAddress = null,
  targetChain = null,

  // For coins (NEW)
  targetType = 'address',
  targetCoinId = null,
  coinName = null,
  coinPlatforms = null, // { chainName: { address: "0x...", isSupportedChain: true } }

  user,
  onLogin
}) => {
  const location = useLocation();
  const [comments, setComments] = useState([]);
  const [mentionedComments, setMentionedComments] = useState([]);
  const [mentionedCount, setMentionedCount] = useState(0); // Count of mentioned comments
  const [activeTab, setActiveTab] = useState('comments'); // 'comments' or 'mentioned'

  // Platform comment aggregation for coin pages
  const [platformComments, setPlatformComments] = useState([]); // Comments from platform addresses
  const [chainFilter, setChainFilter] = useState('all'); // 'all', 'coin', or chain name
  const [platformCommentsLoading, setPlatformCommentsLoading] = useState(false);
  const [replies, setReplies] = useState({}); // { commentId: [replies] }
  const [userVotes, setUserVotes] = useState({}); // { commentId: 'upvote'/'downvote' }
  const [verificationStatus] = useState({}); // { commentId: isVerified }
  const [loading, setLoading] = useState(true);

  // Get user identifier for rate limiting and service calls
  // user.uid is unified: wallet address for wallet users, e_xxx for email users
  const userIdentifier = user?.uid;
  const [expandedThreads, setExpandedThreads] = useState(new Set());
  const [editingComment, setEditingComment] = useState(null);
  const [editingText, setEditingText] = useState(''); // Lifted state to preserve across tab switches
  const [flaggingComment, setFlaggingComment] = useState(null);
  const [flagSuccess, setFlagSuccess] = useState('');
  const [flagError, setFlagError] = useState('');

  // Lifted state for CommentInput to preserve text across tab switches
  const [newCommentText, setNewCommentText] = useState('');

  // State for reply modal
  const [replyingToComment, setReplyingToComment] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [highlightedComment, setHighlightedComment] = useState(null);
  const [aliases, setAliases] = useState({}); // { address: alias }
  const [watchedAddresses, setWatchedAddresses] = useState([]); // Array of watched addresses

  // Smart sort: Verified → Upvotes → Recency
  const smartSortComments = (commentsToSort, verificationMap) => {
    return [...commentsToSort].sort((a, b) => {
      const aVerified = verificationMap[a.id] || false;
      const bVerified = verificationMap[b.id] || false;

      // First priority: Verification status
      if (aVerified !== bVerified) {
        return bVerified ? 1 : -1; // Verified first
      }

      // Second priority: Net upvotes (upvotes - downvotes)
      const aScore = (a.upvotes || 0) - (a.downvotes || 0);
      const bScore = (b.upvotes || 0) - (b.downvotes || 0);
      if (aScore !== bScore) {
        return bScore - aScore; // Higher score first
      }

      // Third priority: Recency (newer first)
      const aTime = new Date(a.createdAt).getTime();
      const bTime = new Date(b.createdAt).getTime();
      return bTime - aTime;
    });
  };

  // Handle real-time comment updates from Firestore listener
  const handleCommentUpdates = useCallback((changes) => {
    setComments(prevComments => {
      let updatedComments = [...prevComments];

      // Handle removed comments
      changes.removed.forEach(removedComment => {
        updatedComments = updatedComments.filter(c => c.id !== removedComment.id);
      });

      // Handle modified comments
      changes.modified.forEach(modifiedComment => {
        const index = updatedComments.findIndex(c => c.id === modifiedComment.id);
        if (index !== -1) {
          updatedComments[index] = modifiedComment;
        }
      });

      // Handle added comments (check for duplicates first)
      changes.added.forEach(addedComment => {
        const exists = updatedComments.some(c => c.id === addedComment.id);
        if (!exists) {
          updatedComments.push(addedComment);

          // AI-generated security assessment notification handled silently
        }
      });

      // Re-sort after updates
      return smartSortComments(updatedComments, {});
    });

    // Stop loading on first snapshot (includes initial data)
    setLoading(false);
  }, []);

  const loadMentionedComments = useCallback(async () => {
    if (!targetAddress) return;

    try {
      const mentioned = await getMentionedComments(targetAddress);

      // COMMENTED OUT: Verified Interactor feature - too heavy (scans all transaction history)
      // // For mentioned comments, we need to check verification with BOTH addresses:
      // // 1. The original target address (where comment was posted)
      // // 2. The current viewing address (the mentioned address)

      // const commenterAddresses = mentioned.map(c => c.authorAddress);

      // // Check verification with current viewing address (the mentioned address)
      // const verificationWithMentioned = await checkMultipleInteractions(targetAddress, commenterAddresses);

      // // Check verification with original target addresses
      // const uniqueTargetAddresses = [...new Set(mentioned.map(c => c.targetAddress))];
      // const verificationWithOriginals = {};

      // for (const targetAddr of uniqueTargetAddresses) {
      //   const commentersForThisTarget = mentioned
      //     .filter(c => c.targetAddress === targetAddr)
      //     .map(c => c.authorAddress);

      //   const verifications = await checkMultipleInteractions(targetAddr, commentersForThisTarget);
      //   Object.assign(verificationWithOriginals, verifications);
      // }

      // // Create commentId -> dual verification map
      // const verificationByCommentId = {};
      // mentioned.forEach(comment => {
      //   const commenterAddr = comment.authorAddress.toLowerCase();
      //   verificationByCommentId[comment.id] = {
      //     withOriginal: verificationWithOriginals[commenterAddr] || false,
      //     withMentioned: verificationWithMentioned[commenterAddr] || false,
      //     originalAddress: comment.targetAddress
      //   };
      // });

      // setVerificationStatus(prev => ({ ...prev, ...verificationByCommentId }));

      // // For sorting mentioned comments, prioritize verification with the mentioned address
      // const sortingMap = {};
      // mentioned.forEach(comment => {
      //   sortingMap[comment.id] = verificationByCommentId[comment.id].withMentioned;
      // });

      // const sortedMentioned = smartSortComments(mentioned, sortingMap);

      // Simplified: No verification, just sort by votes and recency
      const sortedMentioned = smartSortComments(mentioned, {});

      // Filter out:
      // 1. Self-mentions (author mentioning themselves)
      // 2. Comments already on this address's page (targetAddress = current address)
      const filteredMentioned = sortedMentioned.filter(comment => {
        const isSelfMention = comment.authorAddress.toLowerCase() === targetAddress.toLowerCase();
        const isAlreadyOnThisPage = comment.targetAddress.toLowerCase() === targetAddress.toLowerCase();

        return !isSelfMention && !isAlreadyOnThisPage;
      });

      setMentionedComments(filteredMentioned);
      // Update count with accurate filtered count
      setMentionedCount(filteredMentioned.length);

      // Load user votes for mentioned comments in batch
      if (user && filteredMentioned.length > 0) {
        const commentIds = filteredMentioned.map(c => c.id);
        const votes = await getUserVotesBatch(commentIds, user.uid);
        setUserVotes(prev => ({ ...prev, ...votes }));
      }
    } catch (error) {
      console.error('Error loading mentioned comments:', error);
    }
  }, [targetAddress, user]);

  const loadReplies = useCallback(async (commentId) => {
    try {
      const commentReplies = await getReplies(commentId);

      // COMMENTED OUT: Verified Interactor feature - too heavy (scans all transaction history)
      // // Check verification status for reply authors
      // const replyAddresses = commentReplies.map(r => r.authorAddress);
      // const verificationMap = await checkMultipleInteractions(targetAddress, replyAddresses);

      // // Update verification status
      // const newVerificationStatus = {};
      // commentReplies.forEach(reply => {
      //   newVerificationStatus[reply.id] = verificationMap[reply.authorAddress.toLowerCase()] || false;
      // });

      // setVerificationStatus(prev => ({ ...prev, ...newVerificationStatus }));

      // Use chronological order (oldest first) from database query
      setReplies(prev => ({ ...prev, [commentId]: commentReplies }));
      setExpandedThreads(prev => new Set([...prev, commentId]));
    } catch (error) {
      console.error('Error loading replies:', error);
    }
  }, []); // targetAddress removed from dependencies - no longer used after verification feature was disabled

  const loadUserVotes = useCallback(async () => {
    if (!user || comments.length === 0) return;

    // Collect all comment IDs (top-level + replies)
    const commentIds = comments.map(c => c.id);

    // Add reply IDs
    Object.values(replies).forEach(replyList => {
      replyList.forEach(reply => {
        commentIds.push(reply.id);
      });
    });

    // Batch load all votes at once
    const votes = await getUserVotesBatch(commentIds, user.uid);
    setUserVotes(prev => ({ ...prev, ...votes }));
  }, [user, comments, replies]);

  // Subscribe to real-time top-level comments
  useEffect(() => {
    if (targetType === 'address' && (!targetAddress || !targetChain)) return;
    if (targetType === 'coin' && !targetCoinId) return;

    setLoading(true);

    let unsubscribe;

    if (targetType === 'coin') {
      unsubscribe = subscribeToTopLevelCommentsForCoin(
        targetCoinId,
        handleCommentUpdates,
        (error) => {
          console.error('Real-time listener error:', error);
          setLoading(false);
        }
      );
    } else {
      unsubscribe = subscribeToTopLevelComments(
        targetAddress,
        handleCommentUpdates,
        (error) => {
          console.error('Real-time listener error:', error);
          setLoading(false);
        }
      );
    }

    // Cleanup: unsubscribe when component unmounts or dependencies change
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [targetType, targetAddress, targetChain, targetCoinId, handleCommentUpdates]);

  // Load mentioned comments on initial load to show accurate count
  // This loads and filters the data so the count is always correct
  useEffect(() => {
    loadMentionedComments();
  }, [loadMentionedComments]);

  // Track previous target to detect actual changes (not HMR re-runs)
  const prevTargetRef = useRef({ address: targetAddress, coinId: targetCoinId });

  // Reset all comment-related state when target changes (address or coin)
  useEffect(() => {
    const prevTarget = prevTargetRef.current;
    const targetChanged = prevTarget.address !== targetAddress || prevTarget.coinId !== targetCoinId;

    // Only reset if target actually changed, not on HMR re-runs
    if (targetChanged) {
      setComments([]);
      setMentionedComments([]);
      setMentionedCount(0);
      setPlatformComments([]);
      setChainFilter('all');
      setReplies({});
      setUserVotes({});
      setExpandedThreads(new Set());
      setEditingComment(null);
      setEditingText('');
      setNewCommentText('');
      setReplyingToComment(null);
      setReplyText('');
      setFlaggingComment(null);
      setHighlightedComment(null);
      prevTargetRef.current = { address: targetAddress, coinId: targetCoinId };
    }
  }, [targetAddress, targetCoinId]);

  // Load user votes for all visible comments
  useEffect(() => {
    loadUserVotes();
  }, [loadUserVotes]);

  // Load comments from platform addresses for coin pages
  useEffect(() => {
    if (targetType !== 'coin' || !coinPlatforms || Object.keys(coinPlatforms).length === 0) {
      return;
    }

    let isMounted = true;

    const loadPlatformComments = async () => {
      setPlatformCommentsLoading(true);
      try {
        // Fetch comments for all platform addresses in parallel
        const platformEntries = Object.entries(coinPlatforms)
          .filter(([, chainData]) => chainData.address && chainData.isSupportedChain);

        const results = await Promise.all(
          platformEntries.map(async ([chainName, chainData]) => {
            try {
              const addressComments = await getTopLevelComments(chainData.address);
              // Mark each comment with its source
              return addressComments.map(comment => ({
                ...comment,
                sourceType: 'platform',
                sourceChain: chainName
              }));
            } catch (err) {
              console.error(`Error fetching comments for ${chainName}:`, err);
              return [];
            }
          })
        );

        const allPlatformComments = results.flat();

        if (!isMounted) return;

        // Sort by createdAt descending
        allPlatformComments.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt);
          return bTime - aTime;
        });

        setPlatformComments(allPlatformComments);

        // Load user votes for platform comments
        if (user && allPlatformComments.length > 0) {
          const commentIds = allPlatformComments.map(c => c.id);
          const votes = await getUserVotesBatch(commentIds, user.uid);
          if (isMounted) {
            setUserVotes(prev => ({ ...prev, ...votes }));
          }
        }
      } catch (error) {
        console.error('Error loading platform comments:', error);
      } finally {
        if (isMounted) {
          setPlatformCommentsLoading(false);
        }
      }
    };

    loadPlatformComments();

    return () => {
      isMounted = false;
    };
  }, [targetType, coinPlatforms, user]);

  // Auto-refresh replies when replyCount changes for expanded threads
  // This handles the case where another user adds a reply while the thread is expanded
  useEffect(() => {
    comments.forEach(comment => {
      if (expandedThreads.has(comment.id) && replies[comment.id]) {
        const currentReplyCount = replies[comment.id].length;
        const expectedReplyCount = comment.replyCount || 0;

        // If there's a mismatch, reload replies from database
        if (currentReplyCount !== expectedReplyCount) {
          loadReplies(comment.id);
        }
      }
    });
  }, [comments, expandedThreads, replies, loadReplies]);

  // Load aliases when user is available
  useEffect(() => {
    if (!user) return;

    const loadAliases = async () => {
      try {
        const userAliases = await getAllAliases(user.uid);
        setAliases(userAliases);
      } catch (error) {
        console.error('Error loading aliases:', error);
      }
    };

    loadAliases();
  }, [user]);

  // Load watched addresses when user is available
  useEffect(() => {
    if (!user) return;

    const loadWatchedAddresses = async () => {
      try {
        const watched = await getWatchedAddresses();
        setWatchedAddresses(watched);
      } catch (error) {
        console.error('Error loading watched addresses:', error);
      }
    };

    loadWatchedAddresses();
  }, [user]);

  // Handle highlighting comment from URL parameter
  useEffect(() => {
    if (!comments.length) return;

    const highlightCommentId = getHighlightCommentFromURL();

    if (highlightCommentId) {
      // Switch to Comments tab when highlighting a comment
      setActiveTab('comments');
      setHighlightedComment(highlightCommentId);

      // Find if it's a top-level comment or a reply
      const topLevelComment = comments.find(c => c.id === highlightCommentId);

      if (topLevelComment) {
        // It's a top-level comment, scroll to it
        setTimeout(() => {
          const element = document.getElementById(`comment-${highlightCommentId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      } else {
        // Check if it's a reply
        for (const comment of comments) {
          const threadId = comment.id;

          // Load replies for this thread
          loadReplies(threadId).then(() => {
            setTimeout(() => {
              const element = document.getElementById(`comment-${highlightCommentId}`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 300);
          });
        }
      }

      // Clear highlight after 3 seconds
      setTimeout(() => {
        setHighlightedComment(null);
        // Remove URL parameter safely
        updateURL({ highlightComment: null }, true);
      }, 3000);
    }
  }, [comments, loadReplies, location.search]); // Re-run when URL search params change

  const handleCreateComment = async (text) => {
    if (!user) return;

    // Check rate limit before creating comment
    const rateLimitCheck = checkRateLimit(userIdentifier, 'comment_create');
    if (!rateLimitCheck.allowed) {
      throw new Error(rateLimitCheck.message);
    }

    try {
      let newComment;

      if (targetType === 'coin') {
        newComment = await createCoinComment(targetCoinId, text, userIdentifier, null, coinName);
      } else {
        newComment = await createComment(targetAddress, text, userIdentifier, targetChain);
      }

      // Record action for rate limiting
      recordAction(userIdentifier, 'comment_create');

      // Track comment creation
      trackCommentCreate(0, targetType === 'coin' ? 'coin' : targetChain); // level 0 = top-level comment

      // COMMENTED OUT: Verified Interactor feature - too heavy (scans all transaction history)
      // // Check verification for the new commenter
      // const verificationMap = await checkMultipleInteractions(targetAddress, [user.address]);
      // const isVerified = verificationMap[user.address.toLowerCase()] || false;

      // setVerificationStatus(prev => ({ ...prev, [newComment.id]: isVerified }));

      // // Add new comment and re-sort
      // const updatedComments = [newComment, ...comments];
      // const verificationMapForSort = { ...verificationStatus, [newComment.id]: isVerified };
      // const sortedComments = smartSortComments(updatedComments, verificationMapForSort);
      // setComments(sortedComments);

      // Simplified: No verification, just add and re-sort
      const updatedComments = [newComment, ...comments];
      const sortedComments = smartSortComments(updatedComments, {});
      setComments(sortedComments);

      // Auto-upvote the user's own comment
      if (newComment?.id) {
        try {
          await voteComment(newComment.id, userIdentifier, 'upvote');
          setUserVotes(prev => ({ ...prev, [newComment.id]: 'upvote' }));
          // Update local state immediately for instant UI feedback
          // The real-time listener will eventually sync, but this prevents flash of 0
          setComments(prev => prev.map(c =>
            c.id === newComment.id ? { ...c, upvotes: 1 } : c
          ));
        } catch (err) {
          console.warn('Auto-upvote failed:', err);
          // Non-critical, don't throw
        }
      }
    } catch (error) {
      console.error('Error creating comment:', error);
      throw error;
    }
  };

  const handleCreateReply = async (parentComment, text) => {
    if (!user) return;

    // Check rate limit before creating reply
    const rateLimitCheck = checkRateLimit(userIdentifier, 'comment_create');
    if (!rateLimitCheck.allowed) {
      throw new Error(rateLimitCheck.message);
    }

    try {
      let newReply;
      if (targetType === 'coin') {
        newReply = await createCoinComment(targetCoinId, text, userIdentifier, parentComment, coinName);
      } else {
        newReply = await createComment(targetAddress, text, userIdentifier, targetChain, parentComment);
      }

      // Record action for rate limiting
      recordAction(userIdentifier, 'comment_create');

      // Track reply creation
      trackCommentCreate(1, targetType === 'coin' ? 'coin' : targetChain); // level 1 = reply

      // Expand the thread so user can see their new reply
      // For level 0 comments, the thread root is the comment itself
      // For level 1+ comments, use threadRootId or parentId
      const threadId = parentComment.level === 0
        ? parentComment.id
        : (parentComment.threadRootId || parentComment.parentId);
      setExpandedThreads(prev => new Set([...prev, threadId]));

      // Auto-upvote the user's own reply FIRST, before loading replies
      // This ensures the upvote is committed to Firestore before we fetch
      if (newReply?.id) {
        try {
          await voteComment(newReply.id, userIdentifier, 'upvote');
          setUserVotes(prev => ({ ...prev, [newReply.id]: 'upvote' }));
        } catch (err) {
          console.warn('Auto-upvote failed:', err);
          // Non-critical, don't throw
        }
      }

      // Load replies after auto-upvote completes to ensure vote count is included
      // Use a short delay to allow Firestore to index the changes
      setTimeout(() => loadReplies(threadId), 300);

      // Note: The useEffect that monitors replyCount changes will automatically
      // reload replies when the real-time listener updates the parent comment.
    } catch (error) {
      console.error('Error creating reply:', error);
      throw error;
    }
  };

  const handleVote = async (comment, voteType) => {
    if (!user) return;

    // Check rate limit before voting
    const rateLimitCheck = checkRateLimit(userIdentifier, 'vote');
    if (!rateLimitCheck.allowed) {
      console.warn(rateLimitCheck.message);
      return; // Silently ignore vote attempts during cooldown
    }

    try {
      const result = await voteComment(comment.id, userIdentifier, voteType);

      // Record action for rate limiting
      recordAction(userIdentifier, 'vote');

      // Track vote (only track new votes, not removals)
      if (result.action !== 'removed') {
        trackVote(voteType, targetChain);
      }

      // Update user votes map immediately for UI feedback
      if (result.action === 'removed') {
        setUserVotes(prev => {
          const newVotes = { ...prev };
          delete newVotes[comment.id];
          return newVotes;
        });
      } else {
        setUserVotes(prev => ({ ...prev, [comment.id]: voteType }));
      }

      // For top-level comments: real-time Firestore listener updates the counts
      // For replies (level > 0): we need to update locally since replies don't have real-time listeners
      if (comment.level > 0) {
        const threadId = comment.threadRootId || comment.parentId;
        setReplies(prev => {
          const threadReplies = prev[threadId];
          if (!threadReplies) return prev;

          const updatedReplies = threadReplies.map(reply => {
            if (reply.id !== comment.id) return reply;

            const newReply = { ...reply };
            if (result.action === 'added') {
              if (voteType === 'upvote') {
                newReply.upvotes = (newReply.upvotes || 0) + 1;
              } else {
                newReply.downvotes = (newReply.downvotes || 0) + 1;
              }
            } else if (result.action === 'removed') {
              if (voteType === 'upvote') {
                newReply.upvotes = Math.max(0, (newReply.upvotes || 0) - 1);
              } else {
                newReply.downvotes = Math.max(0, (newReply.downvotes || 0) - 1);
              }
            } else if (result.action === 'changed') {
              // Changed from one vote type to another
              if (voteType === 'upvote') {
                newReply.upvotes = (newReply.upvotes || 0) + 1;
                newReply.downvotes = Math.max(0, (newReply.downvotes || 0) - 1);
              } else {
                newReply.downvotes = (newReply.downvotes || 0) + 1;
                newReply.upvotes = Math.max(0, (newReply.upvotes || 0) - 1);
              }
            }
            return newReply;
          });

          return { ...prev, [threadId]: updatedReplies };
        });
      }
    } catch (error) {
      console.error('Error voting:', error);
      alert('Failed to vote. Please try again.');
    }
  };

  const handleEdit = (comment) => {
    setEditingComment(comment);
    setEditingText(comment.text);
  };

  const handleSaveEdit = async (newText) => {
    if (!user || !editingComment) return;

    try {
      await editComment(editingComment.id, newText, userIdentifier);

      // Update local state
      const updateComment = (c) =>
        c.id === editingComment.id
          ? { ...c, text: newText, edited: true, updatedAt: new Date().toISOString() }
          : c;

      if (editingComment.level === 0) {
        setComments(prev => prev.map(updateComment));
      } else {
        setReplies(prev => ({
          ...prev,
          [editingComment.threadRootId]: prev[editingComment.threadRootId]?.map(updateComment) || []
        }));
      }

      // Also update in mentionedComments if present
      setMentionedComments(prev => prev.map(updateComment));

      // Reload mentioned comments to reflect updated mentionedAddresses
      // This ensures newly mentioned addresses will see the comment
      await loadMentionedComments();

      setEditingComment(null);
    } catch (error) {
      console.error('Error editing comment:', error);
      alert('Failed to edit comment. Please try again.');
    }
  };

  const handleDelete = async (comment) => {
    if (!user) return;

    try {
      await deleteComment(comment.id, userIdentifier);

      // Update local state
      const updateComment = (c) =>
        c.id === comment.id ? { ...c, deleted: true, deletedAt: new Date().toISOString() } : c;

      if (comment.level === 0) {
        setComments(prev => prev.map(updateComment));
      } else {
        // Update the reply itself
        setReplies(prev => ({
          ...prev,
          [comment.threadRootId]: prev[comment.threadRootId]?.map(updateComment) || []
        }));

        // Note: Don't decrement replyCount locally - the real-time Firestore listener
        // will receive the updated thread root comment with the decremented count
      }

      // Also update in mentionedComments if present
      setMentionedComments(prev => prev.map(updateComment));
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Failed to delete comment. Please try again.');
    }
  };

  const handleFlag = (comment) => {
    setFlaggingComment(comment);
  };

  const handleSubmitFlag = async (reason, details) => {
    if (!user || !flaggingComment) return;

    // Check rate limit before flagging
    const rateLimitCheck = checkRateLimit(userIdentifier, 'flag');
    if (!rateLimitCheck.allowed) {
      setFlagError(rateLimitCheck.message);
      setTimeout(() => setFlagError(''), 5000);
      return;
    }

    try {
      await flagComment(flaggingComment.id, userIdentifier, reason, details);

      // Record action for rate limiting
      recordAction(userIdentifier, 'flag');

      // Track content flag
      trackContentFlag(reason, flaggingComment.id);

      setFlaggingComment(null);
      setFlagError('');
      setFlagSuccess('Thank you for your report. We will review it shortly.');
      // Clear success message after 4 seconds
      setTimeout(() => setFlagSuccess(''), 4000);
    } catch (error) {
      console.error('Error flagging comment:', error);
      setFlagError('Failed to submit report. Please try again.');
      setTimeout(() => setFlagError(''), 5000);
    }
  };

  const toggleThread = (commentId) => {
    if (expandedThreads.has(commentId)) {
      setExpandedThreads(prev => {
        const newSet = new Set(prev);
        newSet.delete(commentId);
        return newSet;
      });
    } else {
      if (!replies[commentId]) {
        loadReplies(commentId);
      } else {
        setExpandedThreads(prev => new Set([...prev, commentId]));
      }
    }
  };

  // For coin pages: combine coin comments with platform comments
  const combinedComments = React.useMemo(() => {
    if (targetType !== 'coin') {
      return comments;
    }

    // Use a Map to dedupe by comment ID
    const commentMap = new Map();

    // Add coin comments first (with sourceType: 'coin')
    comments.forEach(c => {
      commentMap.set(c.id, { ...c, sourceType: 'coin' });
    });

    // Add platform comments (skip if ID already exists)
    platformComments.forEach(c => {
      if (!commentMap.has(c.id)) {
        commentMap.set(c.id, c); // Already has sourceType: 'platform' from loading
      }
    });

    // Convert to array and apply smart sort (upvotes → recency)
    return smartSortComments(Array.from(commentMap.values()), {});
  }, [targetType, comments, platformComments]);

  // Apply chain filter for coin pages
  const filteredComments = React.useMemo(() => {
    if (targetType !== 'coin' || chainFilter === 'all') {
      return combinedComments;
    }

    if (chainFilter === 'coin') {
      return combinedComments.filter(c => c.sourceType === 'coin');
    }

    // Filter by specific chain
    return combinedComments.filter(c => c.sourceChain === chainFilter);
  }, [targetType, chainFilter, combinedComments]);

  // Get chain options for filter (only chains with comments) - for coin pages
  const chainFilterOptions = React.useMemo(() => {
    if (targetType !== 'coin') return [];

    const options = [];
    const coinCount = combinedComments.filter(c => c.sourceType === 'coin' && !c.deleted).length;
    const chainCounts = {};

    combinedComments.forEach(c => {
      if (c.sourceType === 'platform' && c.sourceChain && !c.deleted) {
        chainCounts[c.sourceChain] = (chainCounts[c.sourceChain] || 0) + 1;
      }
    });

    // Only show filter if there are platform comments
    if (Object.keys(chainCounts).length === 0) return [];

    // Add "All" option
    options.push({ value: 'all', label: 'All', count: combinedComments.filter(c => !c.deleted).length });

    // Add "Coin" option if there are coin comments
    if (coinCount > 0) {
      options.push({ value: 'coin', label: 'Coin', count: coinCount, icon: '🪙' });
    }

    // Add chain options
    Object.entries(chainCounts)
      .sort((a, b) => b[1] - a[1]) // Sort by count descending
      .forEach(([chain, count]) => {
        const chainConfig = getChain(chain);
        options.push({
          value: chain,
          label: chainConfig?.name || chain,
          count,
          icon: chainConfig?.icon || '🔗'
        });
      });

    return options;
  }, [targetType, combinedComments]);

  // Get chain options for filter - for address pages
  const addressChainFilterOptions = React.useMemo(() => {
    if (targetType !== 'address') return [];

    const chainCounts = {};
    const nonDeletedComments = comments.filter(c => !c.deleted);

    nonDeletedComments.forEach(c => {
      const chain = c.chain || c.targetChain || 'ethereum';
      chainCounts[chain] = (chainCounts[chain] || 0) + 1;
    });

    // Always show filter for consistency (even with single chain)
    const options = [];

    // Add "All" option
    options.push({ value: 'all', label: 'All', count: nonDeletedComments.length });

    // Add chain options (or current chain if no comments yet)
    const uniqueChains = Object.keys(chainCounts);
    if (uniqueChains.length === 0 && targetChain) {
      // No comments yet - show current viewing chain
      const chainConfig = getChain(targetChain);
      options.push({
        value: targetChain,
        label: chainConfig?.name || targetChain,
        count: 0,
        icon: chainConfig?.icon || '🔗'
      });
    } else {
      Object.entries(chainCounts)
        .sort((a, b) => b[1] - a[1]) // Sort by count descending
        .forEach(([chain, count]) => {
          const chainConfig = getChain(chain);
          options.push({
            value: chain,
            label: chainConfig?.name || chain,
            count,
            icon: chainConfig?.icon || '🔗'
          });
        });
    }

    return options;
  }, [targetType, targetChain, comments]);

  // Apply chain filter for address pages
  const filteredAddressComments = React.useMemo(() => {
    if (targetType !== 'address' || chainFilter === 'all') {
      return comments;
    }

    // Filter by specific chain
    return comments.filter(c => {
      const commentChain = c.chain || c.targetChain || 'ethereum';
      return commentChain === chainFilter;
    });
  }, [targetType, chainFilter, comments]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <div className="animate-spin inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
        <p className="mt-4 text-gray-600">Loading comments...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Tabs */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2 mb-4">
          <MessageSquare className="w-6 h-6" />
          Community Insights
        </h2>
        <div className="flex gap-1 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('comments')}
            className={`px-4 py-2 font-medium transition ${
              activeTab === 'comments'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Comments ({targetType === 'coin' ? combinedComments.filter(c => !c.deleted).length : comments.filter(c => !c.deleted).length})
          </button>
          {targetType === 'address' && (
            <button
              onClick={() => setActiveTab('mentioned')}
              className={`px-4 py-2 font-medium transition ${
                activeTab === 'mentioned'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Mentioned In ({mentionedCount})
            </button>
          )}
        </div>

        {/* Chain filter - shared for both coin and address pages */}
        {activeTab === 'comments' && (
          <ChainFilterChips
            options={targetType === 'coin' ? chainFilterOptions : addressChainFilterOptions}
            selectedValue={chainFilter}
            onSelect={setChainFilter}
            loading={targetType === 'coin' && platformCommentsLoading}
          />
        )}
      </div>

      {/* Terms & Disclaimer */}
      {/* <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <div className="text-sm font-semibold text-amber-900">Important Notice</div>
            <div className="text-xs text-amber-800 space-y-1">
              <ul className="list-disc list-inside space-y-0.5 ml-2">
                <li>Not publish false, spamming, scamming, inappropriate, discriminative, soliciting or abusive content. This platform has the right to remove any violative content.</li>
                <li>Content on this platform is community-generated and may include AI-generated information. Always verify information independently, as it could be inaccurate. Use it at your own discretion.</li>
                <li>This platform accepts no responsibility for any loss or damage caused by using information from here.</li>
              </ul>
            </div>
          </div>
        </div>
      </div> */}

      {/* Add comment input - only show on Comments tab */}
      {activeTab === 'comments' && (
        <CommentInput
          user={user}
          onSubmit={handleCreateComment}
          onLogin={onLogin}
          placeholder={targetType === 'coin'
            ? `Share your thoughts about ${coinName || 'this coin'}...`
            : "Share your analysis or findings about this address..."}
          text={newCommentText}
          onTextChange={setNewCommentText}
        />
      )}

      {/* Comments list */}
      <div className="space-y-4">
        {activeTab === 'comments' ? (
          (targetType === 'coin' ? filteredComments : filteredAddressComments).length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">
                {chainFilter !== 'all'
                  ? `No comments from ${chainFilter === 'coin' ? 'coin page' : (getChain(chainFilter)?.name || chainFilter)} yet.`
                  : 'No comments yet. Be the first to comment!'}
              </p>
            </div>
          ) : (
            (targetType === 'coin' ? filteredComments : filteredAddressComments)
            .filter(comment => comment && comment.authorAddress && !comment.deleted) // Hide deleted and invalid comments
            .map(comment => (
            <div key={comment.id} id={`comment-${comment.id}`}>
              <Comment
                comment={comment}
                user={user}
                onVote={handleVote}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onFlag={handleFlag}
                onLogin={onLogin}
                userVote={userVotes[comment.id]}
                isVerified={verificationStatus[comment.id] || false}
                level={0}
                targetAddress={targetType === 'coin' ? (comment.targetAddress || null) : targetAddress}
                watchedAddresses={watchedAddresses}
                isHighlighted={highlightedComment === comment.id}
                onReplyClick={(parentComment) => setReplyingToComment(parentComment)}
                coinName={targetType === 'coin' ? coinName : null}
                coinId={targetType === 'coin' ? targetCoinId : null}
              />

              {/* Show replies button */}
              {comment.replyCount > 0 && (
                <div className="ml-12 mt-2">
                  <button
                    onClick={() => toggleThread(comment.id)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {expandedThreads.has(comment.id)
                      ? `▼ Hide ${comment.replyCount} ${comment.replyCount === 1 ? 'reply' : 'replies'}`
                      : `▶ Show ${comment.replyCount} ${comment.replyCount === 1 ? 'reply' : 'replies'}`}
                  </button>
                </div>
              )}

              {/* Replies */}
              {expandedThreads.has(comment.id) && replies[comment.id] && (
                <div>
                  {replies[comment.id]
                    .filter(reply => !reply.deleted) // Hide deleted level 1 comments
                    .map(reply => (
                    <div key={reply.id} id={`comment-${reply.id}`}>
                      <Comment
                        comment={reply}
                        user={user}
                        onVote={handleVote}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onFlag={handleFlag}
                        onLogin={onLogin}
                        userVote={userVotes[reply.id]}
                        isVerified={verificationStatus[reply.id] || false}
                        level={1}
                        targetAddress={targetType === 'coin' ? (comment.targetAddress || null) : targetAddress}
                        watchedAddresses={watchedAddresses}
                        isHighlighted={highlightedComment === reply.id}
                        onReplyClick={(parentComment) => setReplyingToComment(parentComment)}
                        coinName={targetType === 'coin' ? coinName : null}
                        coinId={targetType === 'coin' ? targetCoinId : null}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )))
        ) : (
          // Mentioned In tab
          mentionedComments.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">It hasn't been mentioned in any comments yet.</p>
            </div>
          ) : (
            mentionedComments.map(comment => (
              <div
                key={comment.id}
                className="bg-blue-50 border border-blue-200 rounded-lg p-4"
              >
                <div className="text-xs text-blue-600 mb-2 font-medium flex items-center gap-2">
                  <div className="flex items-center gap-1 flex-1 min-w-0">
                    <span className="break-words">
                      Mentioned in a comment on{' '}
                      <Link
                        to={buildAddressUrl(comment.targetAddress, comment.chain, comment.id)}
                        className="underline break-words hover:text-blue-800"
                      >
                        {aliases[`${comment.targetChain || comment.chain || 'ethereum'}_${comment.targetAddress.toLowerCase()}`] || formatAddress(comment.targetAddress)}
                        {user && user.address && comment.targetAddress.toLowerCase() === user.address.toLowerCase() && (
                          <span className="ml-1 text-xs text-gray-500">(You)</span>
                        )}
                      </Link>
                    </span>
                    <CopyButton text={comment.targetAddress} label="address" />
                  </div>
                </div>
                <Comment
                  comment={comment}
                  user={user}
                  onVote={handleVote}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onFlag={handleFlag}
                  onLogin={onLogin}
                  userVote={userVotes[comment.id]}
                  isVerified={verificationStatus[comment.id] || false}
                  level={0}
                  targetAddress={comment.targetAddress}
                  watchedAddresses={watchedAddresses}
                  showingInMentioned={true}
                  mentionedAddress={targetAddress}
                  dualVerification={verificationStatus[comment.id] || false}
                  disableInteractions={true}
                />
              </div>
            ))
          )
        )}
      </div>

      {/* Chain aggregation explanation */}
      {targetType === 'address' && (
        <div className="text-xs text-gray-400 text-center py-2 border-t border-gray-100">
          Comments for this address are aggregated across all chains. Each comment shows which chain it was posted on.
        </div>
      )}
      {targetType === 'coin' && coinPlatforms && Object.keys(coinPlatforms).length > 0 && (
        <div className="text-xs text-gray-400 text-center py-2 border-t border-gray-100">
          Comments include posts from this coin page and all platform contract addresses. Use the filter above to view by source.
        </div>
      )}

      {/* Edit modal */}
      {editingComment && (
        <CommentModal
          key={editingComment.id}
          title="Edit Comment"
          text={editingText}
          onTextChange={setEditingText}
          onSubmit={handleSaveEdit}
          onCancel={() => { setEditingComment(null); setEditingText(''); }}
          userAddress={user?.address}
          submitButtonText="Save Changes"
          submittingText="Saving..."
          rows={5}
          extraDisableCondition={editingText === editingComment.text}
        />
      )}

      {/* Reply modal */}
      {replyingToComment && (
        <CommentModal
          title={`Reply to ${replyingToComment.authorENS || replyingToComment.authorDisplayName || 'comment'}`}
          text={replyText}
          onTextChange={setReplyText}
          onSubmit={async (text) => {
            await handleCreateReply(replyingToComment, text);
            setReplyingToComment(null);
            setReplyText('');
          }}
          onCancel={() => { setReplyingToComment(null); setReplyText(''); }}
          userAddress={user?.address}
          submitButtonText="Post Reply"
          submittingText="Posting..."
          placeholder="Write your reply..."
        />
      )}

      {/* Flag modal */}
      {flaggingComment && (
        <FlagCommentModal
          comment={flaggingComment}
          onSubmit={handleSubmitFlag}
          onCancel={() => { setFlaggingComment(null); setFlagError(''); }}
          error={flagError}
        />
      )}

      {/* Flag success notification */}
      {flagSuccess && (
        <div className="fixed bottom-4 right-4 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg z-50">
          {flagSuccess}
        </div>
      )}
    </div>
  );
};

// Shared Comment Modal for editing and replying
// text and onTextChange are lifted to parent to preserve state across tab switches
const CommentModal = ({
  title,
  text,
  onTextChange,
  onSubmit,
  onCancel,
  userAddress,
  submitButtonText = 'Submit',
  submittingText = 'Submitting...',
  rows = 4,
  placeholder = '',
  extraDisableCondition = false,  // Additional disable condition (e.g., text === originalText for edit)
}) => {
  // Compute minHeight for preview based on rows (~28px per row)
  const minHeight = `${rows * 28}px`;
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Auto-switch back to write mode when text becomes empty
  useEffect(() => {
    if (!text.trim() && showPreview) {
      setShowPreview(false);
    }
  }, [text, showPreview]);

  const handleSubmit = async () => {
    if (!text.trim() || extraDisableCondition) return;

    // Validate mention count before submitting
    const mentionCount = countMentions(text.trim(), userAddress || '');
    if (mentionCount > 100) {
      alert(`This comment mentions ${mentionCount} addresses. Maximum allowed is 100 unique mentions.`);
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(text.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="bg-white rounded-lg max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-gray-800 mb-4">{title}</h3>

        {/* Tab buttons for Write/Preview */}
        <div className="flex border-b border-gray-200 mb-3">
          <button
            onClick={() => setShowPreview(false)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border-b-2 transition ${
              !showPreview
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Edit3 className="w-3.5 h-3.5" />
            Write
          </button>
          <button
            onClick={() => setShowPreview(true)}
            disabled={!text.trim()}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border-b-2 transition ${
              showPreview
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-not-allowed'
            }`}
          >
            <Eye className="w-3.5 h-3.5" />
            Preview
          </button>
        </div>

        {!showPreview ? (
          <>
            <textarea
              value={text}
              onChange={(e) => onTextChange(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 resize-none"
              rows={rows}
              maxLength={2000}
              autoFocus
              placeholder={placeholder}
            />
            <div className="text-xs text-gray-400 mt-1">
              Supports <a href="https://markdownlivepreview.com/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600 hover:underline">Markdown</a> formatting
            </div>
          </>
        ) : (
          <div
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 overflow-auto prose prose-sm max-w-none"
            style={{ minHeight, maxHeight: '300px' }}
          >
            <MarkdownPreview text={text} />
          </div>
        )}

        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-500">{text.length}/2000</span>

          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!text.trim() || extraDisableCondition || submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
            >
              {submitting ? submittingText : submitButtonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Flag Comment Modal
const FlagCommentModal = ({ comment, onSubmit, onCancel, error }) => {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason) return;

    setSubmitting(true);
    try {
      await onSubmit(reason, details);
    } finally {
      setSubmitting(false);
    }
  };

  const reasons = [
    { value: 'spam', label: 'Spam or misleading' },
    { value: 'harassment', label: 'Harassment or hate speech' },
    { value: 'scam', label: 'Scam or fraud' },
    { value: 'offensive', label: 'Offensive content' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Report Comment</h3>

        <div className="space-y-3 mb-4">
          {reasons.map(r => (
            <label key={r.value} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="reason"
                value={r.value}
                checked={reason === r.value}
                onChange={(e) => setReason(e.target.value)}
                className="w-4 h-4"
              />
              <span className="text-gray-700">{r.label}</span>
            </label>
          ))}
        </div>

        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          placeholder="Additional details (optional)..."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 resize-none"
          rows={3}
        />

        {error && (
          <div className="mt-3 text-sm text-red-600">{error}</div>
        )}

        <div className="flex gap-2 mt-4">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg transition"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!reason || submitting}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition"
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Chain Filter Chips component - shared between coin and address pages
const ChainFilterChips = ({ options, selectedValue, onSelect, loading }) => {
  // Don't render if no options or only "All" option
  if (!options || options.length <= 1) return null;

  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {options.map(option => (
        <button
          key={option.value}
          onClick={() => onSelect(option.value)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition ${
            selectedValue === option.value
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {option.icon && <span>{option.icon}</span>}
          <span>{option.label}</span>
          <span className={`text-xs ${selectedValue === option.value ? 'text-blue-200' : 'text-gray-500'}`}>
            {option.count}
          </span>
        </button>
      ))}
      {loading && (
        <span className="text-xs text-gray-500 self-center ml-2">Loading platform comments...</span>
      )}
    </div>
  );
};

export default CommentSection;
